import React, { useState } from 'react';
import FormInput from './FormInput';
import CopyField from './CopyField';
import KuraNextSteps from './KuraNextSteps';
import { sorgulaBasvuru } from './api';
import { STATUS_STYLES, DEFAULT_STATUS_STYLE, KURA_STYLES, KURA_ROZETLERI } from './constants';

const StatusQuery = ({ showAlert }) => {
  const [query, setQuery] = useState({ tcNo: '', birthDate: '' });
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tcNo') {
      setQuery(prev => ({ ...prev, tcNo: value.replace(/\D/g, '') }));
      return;
    }
    setQuery(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    if (!query.tcNo || !query.birthDate) {
      showAlert('Eksik Bilgi', 'Lütfen T.C. Kimlik Numaranızı ve doğum tarihinizi giriniz.', 'error');
      return;
    }
    if (query.tcNo.length !== 11) {
      showAlert('Hatalı Bilgi', 'T.C. Kimlik Numarası 11 haneli olmalıdır.', 'error');
      return;
    }

    setIsLoading(true);
    setResult(null);
    try {
      const data = await sorgulaBasvuru(query);

      if (data.success) {
        setResult(data);
      } else {
        showAlert(data.title || 'Sonuç Bulunamadı', data.message || 'Başvuru bulunamadı.', 'error');
      }
    } catch (error) {
      showAlert(
        error.response?.data?.title || 'Bağlantı Hatası',
        error.response?.data?.message || 'Sunucu ile iletişim kurulurken bir hata oluştu.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const style = result ? (STATUS_STYLES[result.durum] || DEFAULT_STATUS_STYLE) : DEFAULT_STATUS_STYLE;

  // result.kura yalnızca yönetici sonuçları yayınladıktan sonra dolu gelir
  const kura = result?.kura || null;
  const kuraStyle = kura ? (KURA_STYLES[kura.sonuc] || KURA_STYLES.KAZANAMADI) : null;

  return (
    <div className="animate-fadeIn">
      {/* Kesin kayıt / yedek bilgilendirmesi — sorgulama yapılmadan da görünür.
          Sorgulandıysa yalnızca kişinin kendi durumuna ait kart gösterilir. */}
      {kura ? (
        <KuraNextSteps
          sonuc={kura.sonuc}
          pesinat={result.pesinat}
          konutTipiMetni={result.konutTipiMetni || result.konutTipi}
          showAlert={showAlert}
        />
      ) : (
        <>
          <KuraNextSteps sonuc="ASIL" genel showAlert={showAlert} />
          <KuraNextSteps sonuc="YEDEK" genel showAlert={showAlert} />
        </>
      )}

      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
        Ön Başvuru Durumu Sorgulama
      </h2>
      <p className="text-gray-600 mb-8">
        Ön başvurunuzun güncel durumunu görüntülemek için T.C. Kimlik Numaranızı ve doğum tarihinizi giriniz.
      </p>

      <FormInput
        label="T.C. Kimlik No"
        name="tcNo"
        type="text"
        inputMode="numeric"
        value={query.tcNo}
        onChange={handleChange}
        placeholder="11 haneli TC kimlik numaranızı giriniz"
        maxLength="11"
        required
      />

      <FormInput
        label="Doğum Tarihi"
        name="birthDate"
        type="date"
        value={query.birthDate}
        onChange={handleChange}
        required
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isLoading}
        className={`
          w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg flex items-center justify-center
          ${isLoading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-primary text-white hover:bg-primary-dark hover:shadow-xl active:scale-[0.98]'
          }
        `}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sorgulanıyor...
          </>
        ) : (
          'Sorgula'
        )}
      </button>

      {result && (
        <div className="mt-8 animate-fadeIn">
          {/* Kura Sonucu — açıklandıysa ekranın ilk ve en belirgin bilgisi olur */}
          {kura && (
            <div className={`${kuraStyle.bg} ${kuraStyle.border} border-2 rounded-2xl p-6 mb-6`}>
              <div className="flex items-center mb-3">
                <span className={`${kuraStyle.badge} text-white text-[11px] font-bold tracking-widest px-2.5 py-1 rounded-md`}>
                  {KURA_ROZETLERI[kura.sonuc] || KURA_ROZETLERI.KAZANAMADI}
                </span>
                <span className={`ml-auto text-xs font-semibold uppercase tracking-wider ${kuraStyle.text} opacity-60`}>
                  Kura Sonucu
                </span>
              </div>

              <div className="flex items-start">
                <svg
                  className={`w-8 h-8 mr-3 flex-shrink-0 ${kuraStyle.text}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={kuraStyle.icon} />
                </svg>
                <div>
                  <p className={`text-2xl font-bold leading-tight ${kuraStyle.text}`}>{kura.sonucMetni}</p>
                  <p className={`text-sm mt-2 leading-relaxed ${kuraStyle.text} opacity-90`}>{kura.aciklama}</p>
                </div>
              </div>

              {(kura.siraNo || kura.cekilisNo) && (
                <div className={`grid grid-cols-2 gap-3 mt-5 pt-4 border-t ${kuraStyle.border}`}>
                  {kura.siraNo && (
                    <div>
                      <p className={`text-[11px] uppercase font-bold tracking-wider ${kuraStyle.text} opacity-60`}>
                        {kura.sonuc === 'YEDEK' ? 'Yedek Sıra No' : 'Liste Sıra No'}
                      </p>
                      <p className={`text-lg font-bold ${kuraStyle.text}`}>{kura.siraNo}</p>
                    </div>
                  )}
                  {kura.cekilisNo && (
                    <div>
                      <p className={`text-[11px] uppercase font-bold tracking-wider ${kuraStyle.text} opacity-60`}>
                        Çekiliş No
                      </p>
                      <p className={`text-lg font-bold ${kuraStyle.text}`}>{kura.cekilisNo}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Durum Kartı */}
          <div className={`${style.bg} ${style.border} border-2 rounded-2xl p-5 mb-6`}>
            <div className="flex items-center mb-2">
              <span className={`w-3 h-3 rounded-full ${style.dot} mr-3 flex-shrink-0`}></span>
              <span className={`text-lg font-bold ${style.text}`}>{result.durumMetni}</span>
            </div>
            <p className={`text-sm ${style.text} leading-relaxed`}>{result.durumAciklamasi}</p>
          </div>

          {/* Başvuru Özeti */}
          <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Başvuru No</p>
                <p className="text-gray-900 font-medium">{result.basvuruNo || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Başvuru Tarihi</p>
                <p className="text-gray-900 font-medium">{result.basvuruTarihi || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Konut Tipi</p>
                <p className="text-gray-900 font-medium">{result.konutTipiMetni || result.konutTipi || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Ön Başvuru Bedeli</p>
                <p className="text-gray-900 font-medium">{result.odeme?.tutar || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Peşinat / Aidat</p>
                <p className="text-gray-900 font-medium">{[result.pesinat, result.aidat].filter(Boolean).join(' / ') || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Ad Soyad</p>
                <p className="text-gray-900 font-medium">{result.adSoyad}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">T.C. Kimlik No</p>
                <p className="text-gray-900 font-medium">{result.tcNo}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Telefon</p>
                <p className="text-gray-900 font-medium">{result.telefonNo || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">İl / İlçe</p>
                <p className="text-gray-900 font-medium">{[result.il, result.ilce].filter(Boolean).join(' / ') || '-'}</p>
              </div>
              {result.kuraNo && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Kura No</p>
                  <p className="text-gray-900 font-medium">{result.kuraNo}</p>
                </div>
              )}
              <div className="col-span-2">
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Adres</p>
                <p className="text-gray-900 font-medium leading-relaxed">{result.adres}</p>
              </div>
            </div>
          </div>

          {/* Ödeme bilgileri yalnızca ödeme bekleyen başvurularda */}
          {result.durum === 'pending' && result.odeme && (
            <div className="bg-primary-light p-6 rounded-xl border border-primary/20 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ödeme Bilgileri</h3>

              <div className="flex justify-between items-center py-2 border-b border-primary/10 mb-2">
                <span className="text-gray-700 font-medium">Banka:</span>
                <span className="text-sm font-bold text-gray-900">{result.odeme.banka}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-primary/10 mb-4">
                <span className="text-gray-700 font-medium">Ön Başvuru Bedeli:</span>
                <span className="text-2xl font-bold text-primary">{result.odeme.tutar}</span>
              </div>

              <CopyField
                label="Hesap Sahibi / Alıcı Adı"
                value={result.odeme.hesapSahibi}
                successMessage="Hesap adı kopyalandı!"
                showAlert={showAlert}
                className="font-bold text-[10px] sm:text-xs tracking-tight text-center"
              />

              <CopyField
                label="IBAN Numarası"
                value={result.odeme.iban}
                copyValue={result.odeme.iban.replace(/\s/g, '')}
                successMessage="IBAN kopyalandı!"
                showAlert={showAlert}
                className="font-mono text-sm tracking-tighter text-center"
              />

              <CopyField
                label="Ödeme Açıklaması (otomatik üretildi)"
                value={result.odeme.aciklama}
                successMessage="Açıklama kopyalandı!"
                showAlert={showAlert}
                className="font-mono text-xs sm:text-sm break-all"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusQuery;
